import ContentPanel from '../../display/ui/ContentPanel';
import ScreenContainer from '../../display/ui/ScreenContainer';
import { FONT_TITLE } from '../../constants/AppConstants';
import BtnPollOption from '../buttons/BtnPollOption';
import LoadDisplay from '../../display/ui/LoadDisplay';

export default class ScreenOverlayPoll extends ScreenContainer {
  constructor(game, { locationid }) {
    super(game, { background: 'background_alt_overlay', modal: true });

    // lock location
    this.locationid = locationid;

    // let parent know we are done
    this.onScreenClosed = new Phaser.Signal();

    // has user already submitted?
    this.userSubitted = this.game.gameController.player.getPollSubmitted(this.locationid);

    // create panel to house content
    this.contentPanel = new ContentPanel(this.game, { width: 900, height: 500 });
    this.contentPanel.x = this.game.defaultWidth * 0.5 - this.contentPanel.width * 0.5;
    this.contentPanel.y = this.game.defaultHeight * 0.5 - this.contentPanel.height * 0.5;
    this.contentPanel.onClosePanel.add(this.closePanel, this);
    this.add(this.contentPanel);

    // create a loader, we'll probably need this
    this.loadDisplay = new LoadDisplay(this.game, false);
    this.loadDisplay.scale.set(
      Math.min(1, (this.contentPanel.width / this.loadDisplay.width) * 0.5)
    );
    this.loadDisplay.x = this.contentPanel.width * 0.5 - this.loadDisplay.width * 0.5;
    this.loadDisplay.y = this.contentPanel.height * 0.5 - this.loadDisplay.height * 0.5;
    this.contentPanel.add(this.loadDisplay);

    if (this.game.pollController.ready) {
      //api is ready, hide the loader and start
      this.loadDisplay.visible = false;
      this.displayPoll();
    } else {
      // api is not ready, wait before build
      this.game.pollController.onPollReady.add(this.displayPoll, this);
    }
  }

  displayPoll() {
    this.loadDisplay.visible = false;
    this.game.pollController.onPollReady.remove(this.displayPoll, this);
    // create the question to be displayed
    this.question = this.createQuestion(this.locationid, this.contentPanel.width, 150);
    this.contentPanel.add(this.question);

    // create the options
    this.optionsGroup = this.createPollOptions(this.userSubitted);
    this.optionsGroup.y = 175;
    this.optionsGroup.x = this.contentPanel.width * 0.5 - this.optionsGroup.contentWidth * 0.5;
    this.contentPanel.add(this.optionsGroup);
    if (this.userSubitted) {
      // player has already submitted for this location, display results
      this.submittingPoll = true;
      this.handlePollUpdated(this.game.pollController.getPollResults());
    } else {
      // listen for if the poll updates (in case this screen is displayed before ready)
      this.game.pollController.onPollUpdated.add(this.handlePollUpdated, this);
    }
  }

  /*
  =======================================================================================================
    QUESTION
  =======================================================================================================
  */

  createQuestion(locationid, maxwidth, maxheight) {
    const fontStyle = {
      font: '42px ' + this.game.titleFontStyle,
      fill: '#0090C1',
      align: 'center',
      wordWrap: true,
      wordWrapWidth: maxwidth * 0.8,
      boundsAlignH: 'center',
      boundsAlignV: 'middle',
    };
    const question = this.game.make.text(
      0,
      0,
      this.game.copy.getPollQuestion(locationid),
      fontStyle
    );
    question.setTextBounds(0, 0, maxwidth, maxheight);
    return question;
  }

  /*
  =======================================================================================================
    OPTIONS
  =======================================================================================================
  */

  createPollOptions(userSubitted) {
    const options = [];

    // create container to house options
    const container = this.game.make.group();
    // track width and height of options
    container.contentWidth = 0;
    container.contentHeight = 0;

    // there are 3 options to create
    while (options.length < 3) {
      // create button option
      const option = new BtnPollOption(
        this.game,
        options.length + 1,
        'poll_' + this.locationid.toLowerCase() + '_' + (options.length + 1),
        this.game.copy.getCopyNoDefault(
          'poll_' + this.locationid.toLowerCase() + '_option_' + (options.length + 1)
        )
      );
      // placeholder images are too big and I'm being lazy :)
      option.scale.set(Math.min(1, 250 / option.width));
      // position option
      option.x = container.contentWidth;
      if (!userSubitted) {
        // enable option and listen for select
        option.enable(true);
        option.onSelect.add(this.handleOptionSelected, this);
      }
      // add the option
      container.add(option);
      options.push(option);
      // update container dimensions
      container.contentWidth = option.x + option.width + 25;
      container.contentHeight = Math.max(container.contentHeight, option.height);
    }
    // set options to container for reference later
    container.options = options;
    // adjust for padding :)
    container.contentWidth -= 25;
    // return result
    return container;
  }

  /*
  =======================================================================================================
    USER INTERACTION
  =======================================================================================================
  */

  handleOptionSelected(option) {
    // flag so we know if poll was updated due to user submition
    this.submittingPoll = true;

    // remove listeners
    option.onSelect.remove(this.handleOptionSelected, this);
    this.optionsGroup.forEach((option) => {
      option.enable(false);
    });

    // hide question and options
    this.question.visible = false;
    this.optionsGroup.visible = false;

    // show loading animation
    this.loadDisplay.visible = true;

    // update poll and wait for results
    this.game.pollController.submitPollAnswer(this.locationid, option.id);
  }

  /*
  =======================================================================================================
    POLL EVENTS
  =======================================================================================================
  */

  handlePollUpdated(results) {
    // only update is submittingis flagged so we don't accidently trigger action on user
    if (this.submittingPoll) {
      // set poll sumitted so user can't spam it
      this.game.gameController.player.setPollSubmitted(this.locationid);
      // clear flag and reset display
      this.submittingPoll = false;
      this.loadDisplay.visible = false;
      this.question.visible = true;
      this.optionsGroup.visible = true;
      // if we have results for this lcoation ( we should do )
      if (results[this.locationid]) {
        // grab the answers from the results
        const locationAnswers = results[this.locationid].answers;
        this.optionsGroup.forEach((option) => {
          // for each options, if there is a matching answer, pass through the percentage
          if (locationAnswers[option.id]) {
            option.tweenPercentage(locationAnswers[option.id]);
          }
        });
      }

      //
      this.game.sounds.playSpriteSFX('All_MakesSense', { spriteID: 'general_vo' });
    }
  }

  /*
  =======================================================================================================
    CLOSE
  =======================================================================================================
  */

  closePanel() {
    this.visible = false;
    this.onScreenClosed.dispatch(this);
  }

  /*
  =======================================================================================================
    DESTROY
  =======================================================================================================
  */

  destroy() {
    this.game.pollController.onPollReady.remove(this.displayPoll, this);
    this.game.pollController.onPollUpdated.remove(this.handlePollUpdated, this);
    this.onScreenClosed.removeAll();
    this.onScreenClosed = null;
    this.locationid = null;

    if (this.question) {
      this.question.destroy(true);
      this.question = null;
    }

    if (this.contentPanel) {
      if (this.contentPanel.options) {
        this.contentPanel.options.forEach((option) => {
          option.onSelect.remove(this.handleOptionSelected, this);
          option.destroy(true);
        });
      }

      this.contentPanel.options = null;
      this.contentPanel.destroy(true);
      this.contentPanel = null;
    }

    if (this.loadDisplay) {
      this.loadDisplay.destroy(true);
      this.loadDisplay = null;
    }

    super.destroy(true);
  }
}



// WEBPACK FOOTER //
// ./js/app/ui/overlays/ScreenOverlayPoll.js